import { yupResolver } from '@hookform/resolvers/yup';
import { TicketBookingVenueMeta, TimeBookingVenueMeta } from '@thurstech/commons/dist/models/listing';
import { getPrettyDateString } from '@thurstech/commons/dist/utils/util';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';

import Button from '../../components/button/Button';
import InputSelect, { Option } from '../../components/input/InputSelect';
import { useAppDispatch, useAppSelector } from '../../hooks/store';
import { getProductsThunk } from '../../redux/reducer/listingSlice';

const schema = yup.object().shape({
  listingId: yup.string().required('Product is required'),
  venueId: yup.string(),
  slot: yup.string()
});

type FormData = yup.InferType<typeof schema>;

export interface FilterKey {
  listingId?: string;
  venueId?: string;
  slot?: string;
}

const Filter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const products = useAppSelector(state => state.listing.products);

  useEffect(() => {
    dispatch(getProductsThunk());
  }, [dispatch]);

  const [searchParams] = useSearchParams();
  const listingId = searchParams.get('listingId') ?? undefined;
  const venueId = searchParams.get('venueId') ?? undefined;
  const slot = searchParams.get('slot') ?? undefined;
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors }
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: { listingId, venueId, slot }
  });

  const selectedProductId = watch('listingId');
  const selectedVenueId = watch('venueId');
  const selectedProduct = products.find(p => p.id === selectedProductId);

  const selectedVenue = (selectedProduct?.venues as (TicketBookingVenueMeta | TimeBookingVenueMeta)[])?.find(
    v => v.id === selectedVenueId
  );
  const slots = (selectedVenue as TicketBookingVenueMeta)?.slots;

  const venueOptions =
    selectedProduct?.venues?.map(v => ({
      id: v.id,
      value: v.title
    })) || [];

  const slotOptions: Option[] =
    slots?.map(slot => {
      return {
        id: JSON.stringify(slot.sessions[0][0]),
        value: getPrettyDateString(slot.sessions[0][0])
      };
    }) ?? [];

  const onSubmit = (data: FormData) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('listingId', data.listingId);

    if (data.venueId) {
      searchParams.set('venueId', data.venueId);
    }

    if (data.slot) {
      searchParams.set('slot', data.slot);
    }

    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  const handleReset = () => {
    reset();
    navigate(location.pathname, { replace: true });
  };

  return (
    <div>
      <h3 className="font-semibold mt-4">Filter</h3>
      <form className="mt-4 space-y-4 font-medium" onSubmit={handleSubmit(onSubmit)}>
        <InputSelect
          options={products.map(({ id, name }) => ({ id, value: name }))}
          label="By product"
          placeholder="Select Product"
          register={register}
          errors={errors}
          name="listingId"
        />
        <InputSelect
          label="By venue"
          placeholder="Select venue"
          register={register}
          errors={errors}
          name="venueId"
          options={venueOptions}
        />
        <InputSelect
          label="By slot"
          placeholder="Select slot"
          register={register}
          errors={errors}
          name="slot"
          options={slotOptions}
        />
        <div className="flex gap-2">
          <Button
            type="submit"
            className="px-6 py-2 first-line:items-center  hover:bg-opacity-100 bg-opacity-80 transition duration-200 bg-[#35E0A1] text-black rounded-md"
            label="Filter"
          />
          <Button
            type="button"
            className="px-2 py-2 first-line:items-center  hover:bg-opacity-100 bg-opacity-50 transition duration-200 bg-[#35E0A1] text-black rounded-md"
            label="Clear Filter"
            onClick={handleReset}
          />
        </div>
      </form>
    </div>
  );
};

export default Filter;
